import {
  PatientOrderStatus,
  OPAOrderTypes
} from '../pages/Patients/PatientDashboard/Patient.model'
import { ProgramCountryStatus } from '../constants/program'
import { ProgramCountryDto } from '../types/swaggerTypes'

/**
 * Determines if an initial order should be restricted based on program status and patient status
 * This is used to prevent HCPs from placing initial orders after a program status changes to "closed" or "resupply only"
 *
 * @param programCountryStatus The current status of the program country
 * @param patientOrderStatus The current status of the patient order
 * @param orderType The type of order (initial, resupply, etc.)
 * @returns true if the order should be restricted, false otherwise
 */
export const shouldRestrictInitialOrder = (
  programCountryStatus: ProgramCountryStatus,
  patientOrderStatus: PatientOrderStatus,
  orderType: OPAOrderTypes
): boolean => {
  const isResupplyOnly =
    programCountryStatus === ProgramCountryStatus.OpenForResupply
  const isInitialOrder = orderType === OPAOrderTypes.InitialOrder
  const isRestrictedStatus = [
    PatientOrderStatus.BeginOrder, // "Begin your order"
    PatientOrderStatus.IncompletePAF, // "Continue with your order"
    PatientOrderStatus.IncompleteCheckout // "One more step to go"
  ].includes(patientOrderStatus)

  return isResupplyOnly && isInitialOrder && isRestrictedStatus
}

/**
 * Returns the list of patient statuses that should be restricted from initial orders
 * when a program is in "resupply only" mode
 *
 * @returns Array of PatientOrderStatus values that should be restricted
 */
export const getRestrictedPatientStatuses = (): PatientOrderStatus[] => {
  return [
    PatientOrderStatus.BeginOrder, // "Begin your order"
    PatientOrderStatus.IncompletePAF, // "Continue with your order"
    PatientOrderStatus.IncompleteCheckout // "One more step to go"
  ]
}

/**
 * Checks if a patient order status is one that should be restricted from initial orders
 * when a program is in "resupply only" mode
 *
 * @param patientOrderStatus The status to check
 * @returns true if the status is restricted, false otherwise
 */
export const isRestrictedPatientStatus = (
  patientOrderStatus: PatientOrderStatus
): boolean => {
  return getRestrictedPatientStatuses().includes(patientOrderStatus)
}

/**
 * Determines if a resupply order should be restricted based on program status
 * This is used to prevent HCPs from placing resupply orders when a program is closed
 * or only accepting stock orders
 *
 * @param programCountryStatus The current status of the program country
 * @param orderType The type of order (initial, resupply, stock, etc.)
 * @param isStockOrder Whether this is a stock order
 * @returns true if the resupply order should be restricted, false otherwise
 */
export const shouldRestrictResupplyOrder = (
  programCountryStatus: ProgramCountryStatus,
  orderType: OPAOrderTypes,
  isStockOrder: boolean
): boolean => {
  // If it's a stock order, don't restrict
  if (isStockOrder) {
    return false
  }

  // If program is closed, restrict resupply orders
  const isProgramClosed = programCountryStatus === ProgramCountryStatus.Closed

  // If this is a resupply order and program is closed, restrict it
  const isResupplyOrder = orderType === OPAOrderTypes.Resupply

  return isProgramClosed && isResupplyOrder
}

/**
 * Determines if an order should be restricted based on program country rules from ETL
 * This checks the actual initialOrderType and resupplyOrderType flags from APEX
 *
 * @param programCountry The program country data from ETL
 * @param orderType The type of order being attempted
 * @param isStockOrder Whether this is a stock order
 * @returns true if the order should be restricted, false otherwise
 */
export const shouldRestrictOrderByProgramRules = (
  programCountry: ProgramCountryDto | undefined,
  orderType: OPAOrderTypes,
  isStockOrder: boolean
): boolean => {
  if (!programCountry) {
    return false
  }

  // Stock orders are never restricted if the program allows stock orders
  if (isStockOrder && programCountry.stock === 'Y') {
    return false
  }

  // Check if initial orders are allowed
  if (orderType === OPAOrderTypes.InitialOrder) {
    return programCountry.initialOrderType === 'N'
  }

  // Check if resupply orders are allowed
  if (orderType === OPAOrderTypes.Resupply) {
    return programCountry.resupplyOrderType === 'N'
  }

  return false
}
